import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Check, ChevronRight, Copy, Phone, Search, UserPlus } from 'lucide-react';

import Avatar from '@/components/Avatar';
import Sheet from '@/components/Sheet';
import { apiErrorMessage, apiGet, apiPost } from '@/lib/api';
import type { BackState } from '@/lib/back-nav';
import { formatBps, formatDate } from '@/lib/format';
import { ROLE_LABEL } from '@/lib/sales';
import { pluralEstablishments } from '@/lib/subscription';
import { useAuthStore, useIsSalesDirector, type ManagerRole } from '@/store/auth.store';

interface ManagerItem {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  code: string;
  role: ManagerRole;
  status: 'active' | 'blocked';
  commissionBps: number;
  avatarUrl?: string | null;
  establishments: number;
  payingEstablishments: number;
  lastLoginAt: string | null;
  createdAt: string;
  createdBy: { id: string; name: string } | null;
  referralLink: string;
}

const LIST_BACK: BackState = { backTo: '/managers', backSteps: 1, backLabel: 'Команда' };

/** Себя — в личный кабинет; карточку руководителя главный не открывает. */
function cardHref(m: ManagerItem, myId: string | undefined, isDirector: boolean): string | null {
  if (m.id === myId) return '/me';
  if (m.role === 'SALES_DIRECTOR' && !isDirector) return null;
  return `/managers/${m.id}`;
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

export default function Managers() {
  const isDirector = useIsSalesDirector();
  const myId = useAuthStore((s) => s.manager?.id);
  const [items, setItems] = useState<ManagerItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [creating, setCreating] = useState(false);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    void apiGet<ManagerItem[]>('/managers', { silent: true })
      .then((rows) => {
        if (!cancelled) setItems(rows);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Не удалось загрузить менеджеров'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [version]);

  const needle = query.trim().toLowerCase();
  const visible = useMemo(
    () =>
      needle
        ? items.filter((m) => `${m.name} ${m.email} ${m.code} ${m.phone ?? ''}`.toLowerCase().includes(needle))
        : items,
    [items, needle],
  );

  const onCopy = async (m: ManagerItem) => {
    if (await copyText(m.referralLink)) toast.success(`Ссылка ${m.code} скопирована`);
    else toast.error('Не удалось скопировать — откройте карточку и скопируйте вручную');
  };

  return (
    <div className="max-w-5xl space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold">Менеджеры</h1>
          <p className="mt-1 text-sm text-ink/55">
            {items.length ? `${items.length} в команде` : 'Команда пока пустая'}
          </p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
          <UserPlus size={16} /> {isDirector ? 'Добавить сотрудника' : 'Добавить менеджера'}
        </button>
      </div>

      {items.length > 5 ? (
        <div className="relative">
          <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-ink/35" />
          <input
            className="input !pl-9"
            type="search"
            placeholder="Имя, email или код"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Поиск менеджера"
          />
        </div>
      ) : null}

      {loading ? <div className="text-ink/50 text-sm">Загрузка…</div> : null}
      {error ? <div className="card p-4 text-sm text-ink/70">{error}</div> : null}

      {!loading && !error && visible.length === 0 ? (
        <div className="card p-6 text-sm text-ink/55">
          {items.length ? 'Никого не нашли.' : 'Добавьте первого менеджера — он войдёт по коду на свой email.'}
        </div>
      ) : null}

      {visible.length > 0 ? (
        <div className="card divide-y divide-line/60">
          {visible.map((m) => {
            const href = cardHref(m, myId, isDirector);
            return (
            <div key={m.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex min-w-0 items-start gap-3">
              <Avatar name={m.name} url={m.avatarUrl} size={40} className="mt-0.5" />
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-1.5">
                  {href ? (
                    <Link to={href} state={LIST_BACK} className="font-medium hover:text-orange">
                      {m.name}
                    </Link>
                  ) : (
                    <span className="font-medium">{m.name}</span>
                  )}
                  {m.id === myId ? <span className="text-xs text-ink/40">это вы</span> : null}
                  {m.role !== 'MANAGER' ? (
                    <span className="rounded-md bg-orange/10 px-1.5 py-0.5 text-[11px] font-medium text-orange-700">
                      {ROLE_LABEL[m.role]}
                    </span>
                  ) : null}
                  {m.status === 'blocked' ? (
                    <span className="rounded-md bg-rose-50 px-1.5 py-0.5 text-[11px] font-medium text-rose-700">
                      заблокирован
                    </span>
                  ) : null}
                </div>
                <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-ink/55">
                  <a href={`mailto:${m.email}`} className="hover:text-orange">{m.email}</a>
                  {m.phone ? (
                    <a href={`tel:${m.phone.replace(/[^\d+]/g, '')}`} className="inline-flex items-center gap-1 hover:text-orange">
                      <Phone size={12} /> {m.phone}
                    </a>
                  ) : null}
                </div>
                <div className="text-xs text-ink/45">
                  {m.establishments} {pluralEstablishments(m.establishments)}
                  {m.establishments ? ` · платят ${m.payingEstablishments}` : ''} · ставка {formatBps(m.commissionBps)} ·{' '}
                  {m.lastLoginAt ? `вход ${formatDate(m.lastLoginAt)}` : 'ещё не входил'}
                  {m.createdBy ? ` · добавил ${m.createdBy.name}` : ''}
                </div>
              </div>
              </div>
              <div className="ml-[52px] flex shrink-0 items-center gap-2 self-start sm:ml-0 sm:self-auto">
                <button
                  type="button"
                  className="btn border border-line bg-white text-ink/80 hover:bg-paper"
                  onClick={() => void onCopy(m)}
                >
                  <Copy size={15} />
                  <span className="font-mono text-xs">{m.code}</span>
                </button>
                {href ? (
                  <Link to={href} state={LIST_BACK} className="btn border border-line bg-white text-ink/80 hover:bg-paper">
                    {m.id === myId ? 'Кабинет' : 'Карточка'} <ChevronRight size={15} />
                  </Link>
                ) : null}
              </div>
            </div>
            );
          })}
        </div>
      ) : null}

      {creating ? (
        <CreateManagerSheet
          canPickRole={isDirector}
          onClose={() => setCreating(false)}
          onCreated={() => setVersion((v) => v + 1)}
        />
      ) : null}
    </div>
  );
}

/** Код для ?ref= из email: латиница, цифры, «-» и «_», как normalizeManagerCode на сервере. */
function suggestCode(email: string): string {
  const local = email.split('@')[0] ?? '';
  const code = local.toUpperCase().replace(/[^A-Z0-9_-]/g, '').slice(0, 24);
  return code.length >= 2 ? code : '';
}

type NewRole = 'MANAGER' | 'HEAD_MANAGER';

const NEW_ROLE_LABEL: Record<NewRole, string> = {
  MANAGER: 'Менеджер',
  HEAD_MANAGER: 'Главный менеджер',
};

function CreateManagerSheet({
  canPickRole,
  onClose,
  onCreated,
}: {
  /** Главного менеджера заводит только руководитель отдела продаж. */
  canPickRole: boolean;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [role, setRole] = useState<NewRole>('MANAGER');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [codeTouched, setCodeTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ code: string; referralLink: string } | null>(null);
  const [copied, setCopied] = useState(false);

  const effectiveCode = codeTouched ? code : suggestCode(email);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiPost<{ id: string; code: string; referralLink: string }>(
        '/managers',
        {
          email: email.trim(),
          name: name.trim(),
          phone: phone.trim() || null,
          code: effectiveCode.trim(),
          ...(canPickRole ? { role } : {}),
        },
        { silent: true },
      );
      setCreated({ code: res.code, referralLink: res.referralLink });
      onCreated();
    } catch (err) {
      setError(apiErrorMessage(err, 'Не удалось добавить менеджера'));
    } finally {
      setBusy(false);
    }
  };

  if (created) {
    return (
      <Sheet title={role === 'HEAD_MANAGER' ? 'Главный менеджер добавлен' : 'Менеджер добавлен'} onClose={onClose}>
        <div className="space-y-4">
          <p className="text-sm text-ink/60">
            Он входит на manager.resstro.ru по коду, который придёт на {email.trim()}. Передайте ему
            реферальную ссылку:
          </p>
          <input className="input text-xs" readOnly value={created.referralLink} onFocus={(e) => e.target.select()} />
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              className="btn border border-line bg-white text-ink hover:bg-paper"
              onClick={async () => {
                if (await copyText(created.referralLink)) setCopied(true);
                else toast.error('Не удалось скопировать — выделите ссылку вручную');
              }}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Скопировано' : 'Копировать ссылку'}
            </button>
            <button type="button" className="btn-primary" onClick={onClose}>
              Готово
            </button>
          </div>
        </div>
      </Sheet>
    );
  }

  return (
    <Sheet title={canPickRole ? 'Новый сотрудник' : 'Новый менеджер'} onClose={onClose}>
      <form className="space-y-3" onSubmit={onSubmit}>
        {canPickRole ? (
          <fieldset>
            <legend className="label">Роль</legend>
            <div className="grid grid-cols-2 gap-2">
              {(['MANAGER', 'HEAD_MANAGER'] as NewRole[]).map((r) => (
                <button
                  key={r}
                  type="button"
                  aria-pressed={role === r}
                  onClick={() => setRole(r)}
                  className={
                    role === r
                      ? 'btn border border-orange bg-orange/10 text-ink'
                      : 'btn border border-line text-ink/70 hover:border-ink/30'
                  }
                >
                  {NEW_ROLE_LABEL[r]}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-ink/50">
              {role === 'HEAD_MANAGER'
                ? 'Видит все заведения, продлевает доступ, принимает наличные и добавляет менеджеров.'
                : 'Видит только свои заведения и свои начисления.'}
            </p>
          </fieldset>
        ) : null}
        <div>
          <label className="label" htmlFor="mgr-email">Email</label>
          <input
            id="mgr-email"
            className="input"
            type="email"
            autoComplete="off"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="mgr-name">Имя</label>
          <input
            id="mgr-name"
            className="input"
            required
            minLength={2}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="mgr-phone">Телефон</label>
          <input
            id="mgr-phone"
            className="input"
            type="tel"
            inputMode="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </div>
        <div>
          <label className="label" htmlFor="mgr-code">Код для ссылки ?ref=</label>
          <input
            id="mgr-code"
            className="input font-mono uppercase"
            required
            minLength={2}
            maxLength={24}
            value={effectiveCode}
            onChange={(e) => {
              setCodeTouched(true);
              setCode(e.target.value.toUpperCase());
            }}
          />
          <p className="mt-1 text-xs text-ink/45">Латиница, цифры, «-» и «_». Код должен быть свободен.</p>
        </div>
        <p className="rounded-lg bg-paper px-3 py-2.5 text-xs text-ink/60">
          {canPickRole
            ? 'Ставка — 20 % по умолчанию, её меняет администратор Resstro.'
            : 'Ставка — 20 % по умолчанию. Изменить ставку или роль может администратор Resstro.'}
        </p>
        {error ? <div className="rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</div> : null}
        <button type="submit" className="btn-primary w-full" disabled={busy}>
          <UserPlus size={16} />
          {busy ? 'Добавляем…' : 'Добавить'}
        </button>
      </form>
    </Sheet>
  );
}
